import { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { connect } from "react-redux";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { type Dispatch, type UnknownAction } from "redux";
import { v4 as uuidv4 } from "uuid";

import { Badge } from "@/components/ui/badge";
import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  addOfferComment,
  API_URL_DEPOSIT_DETAILS,
  createMarketOrder,
  listOfferComments,
  listOfferCommentsReset,
  listOfferNegotiationProposals,
  listOfferNegotiationProposalsReset,
  listRecalls,
  listRejections,
  updateDeposit,
  updateMarketOffer,
} from "../../../../actions";
import client from "../../../../client";
import i18n from "../../../../i18n";
import { OFFER_TYPES } from "../../../../constants";
import { formatNumber, getCurrencySymbolFromCache } from "../../../../utils";
import {
  deliveryCostAmount,
  effectiveDeliveryCost,
  isDemandLine,
} from "../../../../utils/delivery-cost";
import { UserGroup } from "../../../../utils/roles";
import {
  canRecallOffer,
  closingAcceptsDeliveryCost,
  exchangeRound,
  isProposalFromTrader,
  offerAwaitingParty,
  offerStatusLabelKey,
  sortByCreationTime,
  toOfferUpdatePayload,
} from "../../utils";
import { DeliveryLookups } from "../../types";
import { Deposit } from "../../../Deposits/DepositCard/deposit-dtos";
import { Offer, OfferPaymentTimeOption, OfferStatus } from "../OfferCard/interfaces";
import { NegotiationProposal } from "./NegotiatedDetailsCard/offer-negotiation-proposal";
import { OfferComment, OfferCommentDto } from "./OfferCommentCard";
import { buildDepositSplit } from "./depositSplit";
import { commentSchema, OfferCommentForm, OfferCommentFormFields } from "./OfferCommentForm";
import { OfferDetailRail } from "./OfferDetailRail";
import { OfferTermsComparison, OfferTermsHeadline } from "./OfferTermsComparison";
import { ProposalComment } from "./ProposalComment";
import { WaitingForResponse } from "./WaitingForResponse";
import Loading from "@/components/Loading";

// What the parent (OffersList) passes in.
interface OfferCommentsModalOwnProps {
  offer: Offer;
  deliveryLookups: DeliveryLookups;
  readOnly?: boolean;
  onCancel: () => void;
  onRecallOffer: () => Promise<void>;
  onSendBackExchange: () => Promise<void>;
  onCounterOffer: (proposal: NegotiationProposal) => void;
  setSeeReasonsOffer: (offer: Offer) => void;
}

interface StateProps {
  negotiationProposals: NegotiationProposal[];
  commentsList: OfferCommentDto[];
  isLoading: boolean;
  isAcceptingProposalPending: boolean;
}

interface DispatchProps {
  addOfferComment: (text: string, offer: Offer) => Promise<unknown>;
  onAcceptOffer: (offer: Offer, proposal: NegotiationProposal) => Promise<void>;
  refreshContent: (offer: Offer) => void;
}

type OfferCommentsModalProps = OfferCommentsModalOwnProps & StateProps & DispatchProps;

// The terms themselves live in the comparison table; the thread only carries words.
type ThreadItem =
  | { kind: "comment"; time: string; comment: OfferCommentDto }
  | { kind: "proposal"; time: string; proposal: NegotiationProposal; round: number };

const TAB_COMPARISON = "comparison";
const TAB_COMMENTS = "comments";

export const OfferCommentsModalView = ({
  offer,
  deliveryLookups,
  readOnly = false,
  onCancel,
  onRecallOffer,
  onSendBackExchange,
  onCounterOffer,
  negotiationProposals,
  commentsList,
  isLoading,
  isAcceptingProposalPending,
  addOfferComment,
  onAcceptOffer,
  refreshContent,
}: OfferCommentsModalProps) => {
  const { t } = useTranslation();

  const methods = useForm<OfferCommentFormFields>({
    defaultValues: { comment: "" },
    resolver: zodResolver(commentSchema),
    reValidateMode: "onSubmit",
  });

  const [tab, setTab] = useState(TAB_COMPARISON);
  const [confirmingAccept, setConfirmingAccept] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView(false);
  }, [commentsList, negotiationProposals, isLoading, tab]);

  const deliveryRound = exchangeRound(offer, negotiationProposals);
  const rounds = deliveryRound ? [...negotiationProposals, deliveryRound] : negotiationProposals;

  const [round, setRound] = useState(rounds.length - 1);

  // A counter offer submitted while the modal is open must land on the newest round.
  useEffect(() => setRound(rounds.length - 1), [rounds.length]);

  const selectedProposal: NegotiationProposal | undefined = rounds[round];
  const isLatestRound = round === rounds.length - 1;
  const lastProposal = negotiationProposals[negotiationProposals.length - 1];

  const showActions =
    !!lastProposal &&
    !isProposalFromTrader(lastProposal) &&
    (offer?.status === OfferStatus.Negotiating ||
      offer?.status === OfferStatus.ExchangeDetailsUpdated);

  const awaitingParty = offerAwaitingParty(offer);
  const isAwaitingBuyer = readOnly && awaitingParty === "you";
  const waitingMessageKey =
    awaitingParty === "aggregator"
      ? "commentsModal.waitingForResponse"
      : isAwaitingBuyer
        ? "commentsModal.waitingForOrganisation"
        : offer?.status === OfferStatus.OfferDone
          ? "commentsModal.waitingForDelivery"
          : offer?.status === OfferStatus.ExchangePaymentPending
            ? "commentsModal.waitingForPayment"
            : undefined;

  const isExchangeUpdate = offer?.status === OfferStatus.ExchangeDetailsUpdated;
  const showRespondActions =
    showActions && isLatestRound && !!selectedProposal && selectedProposal !== deliveryRound;
  const isSchoolOffer = !!offer?.is_school_offer;
  const canRecall =
    !isSchoolOffer && (canRecallOffer(offer?.status) || (showActions && !isExchangeUpdate));

  // The offer's own comment opens the thread; it has no comment record of its own.
  const openedAt = offer?.publishing_datetime ?? offer?.publishing_date;
  const openingComment: OfferCommentDto | undefined = offer?.comment
    ? {
        text: offer.comment,
        uuid: "opening-comment",
        trader_market_offer_id: offer.id,
        trader_market_offer_status: OfferStatus.New,
        backend_creation_time: openedAt,
        frontend_creation_time: openedAt,
        user: {
          first_name: offer.trader_representative?.person?.first_name,
          last_name: offer.trader_representative?.person?.last_name,
          roles: [UserGroup.TRADER_REPR],
        },
      }
    : undefined;

  const threadItems: ThreadItem[] = [
    ...(openingComment
      ? [
          {
            kind: "comment" as const,
            time: openingComment.backend_creation_time,
            comment: openingComment,
          },
        ]
      : []),
    ...(commentsList || []).map(comment => ({
      kind: "comment" as const,
      time: comment.backend_creation_time,
      comment,
    })),
    ...(negotiationProposals || [])
      .map((proposal, index) => ({
        kind: "proposal" as const,
        time: proposal.backend_creation_time,
        proposal,
        round: index + 1,
      }))
      .filter(({ proposal }) => !!proposal.text),
  ].sort((a, b) => Date.parse(a.time) - Date.parse(b.time));

  const onAddOfferSubmit = (values: OfferCommentFormFields): Promise<unknown> =>
    addOfferComment(values.comment, offer).then(() => refreshContent(offer));

  // Accepting a demand's last standing line also accepts the aggregator's pending delivery cost.
  const acceptedDeliveryCost = closingAcceptsDeliveryCost(offer);
  const confirmsDeliveryCost = acceptedDeliveryCost !== null && acceptedDeliveryCost !== undefined;

  const acceptSelectedProposal = () => {
    setConfirmingAccept(false);
    if (selectedProposal) {
      onAcceptOffer(offer, selectedProposal);
    }
  };

  const triggerClassName =
    "h-auto flex-none rounded-none px-0 pb-3 text-base group-data-[orientation=horizontal]/tabs:after:bottom-[-1px]";

  return (
    <Dialog open={!!offer} onOpenChange={open => !open && onCancel()}>
      <DialogContent className="grid h-dvh max-h-dvh w-screen max-w-none grid-rows-[auto_minmax(0,1fr)] gap-0 overflow-hidden rounded-none border-0 p-0 sm:h-auto sm:max-h-[90vh] sm:w-full sm:rounded-lg sm:border md:max-w-5xl md:grid-cols-[minmax(0,17rem)_minmax(0,1fr)] md:grid-rows-[minmax(0,1fr)]">
        <DialogHeader className="sr-only">
          <DialogTitle>{t("offerCard.viewComments")}</DialogTitle>
          <DialogDescription>
            {t("offerDetail.dialogDescription", { id: offer?.id })}
          </DialogDescription>
        </DialogHeader>

        <OfferDetailRail
          offer={offer}
          proposals={rounds}
          round={round}
          onRoundChange={setRound}
          readOnly={readOnly}
        />

        <div className="flex min-h-0 flex-col">
          <OfferTermsHeadline offer={offer} proposal={selectedProposal} />

          <Tabs
            value={tab}
            onValueChange={setTab}
            className="flex min-h-0 flex-1 flex-col gap-0 overflow-hidden"
          >
            <TabsList
              variant="line"
              className="w-full justify-start gap-6 rounded-none border-border border-b bg-transparent p-0 px-6 group-data-[orientation=horizontal]/tabs:h-auto"
            >
              <TabsTrigger value={TAB_COMPARISON} className={triggerClassName}>
                {t("termsComparison.sideBySide")}
              </TabsTrigger>
              {!isSchoolOffer && (
                <TabsTrigger value={TAB_COMMENTS} className={triggerClassName}>
                  {t("commentsModal.comments")}
                  {threadItems.length > 0 && <Badge>{threadItems.length}</Badge>}
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value={TAB_COMPARISON} className="min-h-0 overflow-y-auto">
              <OfferTermsComparison
                offer={offer}
                proposals={rounds}
                deliveryLookups={deliveryLookups}
                round={round}
                onRoundChange={setRound}
              />
              {waitingMessageKey && !isLoading && (
                <div className="px-6">
                  <WaitingForResponse
                    message={t(waitingMessageKey, {
                      organisation: isAwaitingBuyer
                        ? offer?.trader?.name
                        : offer?.tagged_aggregator?.name,
                    })}
                  />
                </div>
              )}
            </TabsContent>

            {!isSchoolOffer && (
              <TabsContent
                value={TAB_COMMENTS}
                className="min-h-0 flex-col data-[state=active]:flex"
              >
                <div className="flex min-h-40 flex-1 flex-col overflow-y-auto px-6 pt-4">
                  {isLoading || isAcceptingProposalPending ? (
                    <Loading />
                  ) : threadItems.length === 0 ? (
                    <div className="flex flex-1 items-center justify-center text-muted-foreground text-sm">
                      {t("termsComparison.noComments")}
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      {threadItems.map(item =>
                        item.kind === "proposal" ? (
                          <ProposalComment
                            key={item.proposal.uuid}
                            proposal={item.proposal}
                            round={item.round}
                          />
                        ) : (
                          <OfferComment
                            key={item.comment.uuid}
                            statusLabel={t(
                              offerStatusLabelKey(
                                item.comment.trader_market_offer_status as OfferStatus
                              )
                            )}
                            comment={item.comment}
                          />
                        )
                      )}
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {!readOnly && (
                  <div className="px-6 pb-4">
                    <OfferCommentForm onValidate={onAddOfferSubmit} methods={methods} />
                  </div>
                )}
              </TabsContent>
            )}
          </Tabs>

          {!readOnly && (canRecall || showRespondActions) && (
            <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-2 border-border border-t bg-background px-6 py-3">
              {canRecall ? (
                <WFPButton variant="destructive-secondary" onClick={onRecallOffer}>
                  {t("offerCard.recallOffer")}
                </WFPButton>
              ) : (
                <span />
              )}
              {showRespondActions && selectedProposal && (
                <div className="flex flex-1 flex-wrap justify-end gap-2">
                  {isExchangeUpdate ? (
                    <WFPButton
                      className="flex-1 sm:flex-none"
                      variant="secondary"
                      onClick={onSendBackExchange}
                    >
                      {t("negotiationCard.disputeQuantity")}
                    </WFPButton>
                  ) : (
                    <WFPButton
                      className="flex-1 sm:flex-none"
                      variant="secondary"
                      onClick={() => onCounterOffer(selectedProposal)}
                    >
                      {t("negotiationCard.counterOffer")}
                    </WFPButton>
                  )}
                  <WFPButton
                    className="flex-1 sm:flex-none"
                    onClick={() =>
                      confirmsDeliveryCost
                        ? setConfirmingAccept(true)
                        : onAcceptOffer(offer, selectedProposal)
                    }
                  >
                    {isExchangeUpdate
                      ? t("negotiationCard.confirmQuantity")
                      : t("negotiationCard.accept")}
                  </WFPButton>
                </div>
              )}
            </div>
          )}
        </div>

        <Dialog open={confirmingAccept} onOpenChange={setConfirmingAccept}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("deliveryCostNegotiation.lastLineTitle")}</DialogTitle>
              <DialogDescription>
                {t("deliveryCostNegotiation.lastLineWarning", {
                  amount: `${getCurrencySymbolFromCache() || offer?.currency?.symbol || ""}${formatNumber(
                    deliveryCostAmount(acceptedDeliveryCost)
                  )}`,
                })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <WFPButton
                type="button"
                variant="secondary"
                onClick={() => setConfirmingAccept(false)}
              >
                {t("common.cancel")}
              </WFPButton>
              <WFPButton type="button" onClick={acceptSelectedProposal}>
                {t("negotiationCard.accept")}
              </WFPButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

interface ListSlice<T> {
  isFetching: boolean;
  isResetting: boolean;
  data: { results: T[] };
}

interface ModalState {
  listRecalls: ListSlice<unknown>;
  listRejections: ListSlice<unknown>;
  listOfferComments: ListSlice<OfferCommentDto>;
  listOfferNegotiationProposals: ListSlice<NegotiationProposal>;
}

const mapStateToProps = (state: ModalState): StateProps => ({
  commentsList: state.listOfferComments && state.listOfferComments.data.results,
  negotiationProposals: state.listOfferNegotiationProposals
    ? sortByCreationTime(state.listOfferNegotiationProposals.data.results)
    : [],
  isLoading:
    state.listRecalls.isFetching ||
    state.listRejections.isFetching ||
    state.listOfferComments.isFetching ||
    state.listOfferNegotiationProposals.isFetching,
  isAcceptingProposalPending:
    state.listRecalls.isFetching ||
    state.listRejections.isFetching ||
    state.listOfferComments.isFetching ||
    state.listOfferComments.isResetting ||
    state.listOfferNegotiationProposals.isFetching ||
    state.listOfferNegotiationProposals.isResetting,
});

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown): Promise<unknown> =>
  dispatch(action as UnknownAction) as unknown as Promise<unknown>;

const mapDispatchToProps = (
  dispatch: AppDispatch,
  ownProps: OfferCommentsModalOwnProps
): DispatchProps => ({
  addOfferComment: (text: string, offer: Offer) =>
    dispatchAction(
      dispatch,
      addOfferComment({
        trader_market_offer: offer.id,
        text,
        uuid: uuidv4(),
        trader_market_offer_status: offer.status,
      })
    ),
  onAcceptOffer: async (offer: Offer, negotiationProposal: NegotiationProposal) => {
    dispatchAction(dispatch, listOfferCommentsReset());
    dispatchAction(dispatch, listOfferNegotiationProposalsReset());

    // A demand line's cost is the demand's: accepting the line settles none of its own.
    const demandLine = isDemandLine(offer);
    const newOffer: Offer = {
      ...offer,
      price: negotiationProposal.price,
      status:
        offer.status === OfferStatus.ExchangeDetailsUpdated
          ? offer.payment_time_option === OfferPaymentTimeOption.ReceiveUponExchange
            ? OfferStatus.ExchangeDone
            : OfferStatus.ExchangePaymentPending
          : offer.offer_type === OFFER_TYPES.DIRECT_OFFER
            ? OfferStatus.OfferDone
            : OfferStatus.Accepted,
      planned_exchange_time: negotiationProposal.planned_exchange_time,
      exchange_confirmation_time:
        negotiationProposal.proposal_type === "EXCHANGE_NEGOTIATION"
          ? new Date().toISOString()
          : undefined,
      minimum_quantity: negotiationProposal.minimum_quantity,
      maximum_quantity: negotiationProposal.maximum_quantity,
      delivery_cost: demandLine ? null : effectiveDeliveryCost(negotiationProposal, offer),
      delivery_location: negotiationProposal.delivery_location,
      custom_delivery_location: negotiationProposal.custom_delivery_location,
      custom_delivery_location_type: negotiationProposal.custom_delivery_location_type,
    };

    if (
      offer.status === OfferStatus.ExchangeDetailsUpdated ||
      offer.status === OfferStatus.Negotiating
    ) {
      dispatchAction(
        dispatch,
        addOfferComment({
          trader_market_offer: newOffer.id,
          text:
            offer.status === OfferStatus.ExchangeDetailsUpdated
              ? i18n.t("autoComment.confirmExchangeUpdate", {
                  trader: newOffer.trader.name,
                  unit: newOffer.unit_of_measure.code,
                  previousQuantity: formatNumber(offer.minimum_quantity),
                  newQuantity: formatNumber(newOffer.minimum_quantity),
                })
              : i18n.t("autoComment.confirmInitialNegotiation", {
                  organization: offer.trader.name,
                  details: `${i18n.t("autoComment.proposalDetailsPresentation", {
                    quantity:
                      !newOffer.maximum_quantity ||
                      newOffer.maximum_quantity === newOffer.minimum_quantity
                        ? formatNumber(newOffer.minimum_quantity)
                        : `${formatNumber(newOffer.minimum_quantity)} - ${formatNumber(
                            newOffer.maximum_quantity
                          )}`,
                    unit: newOffer.unit_of_measure.code,
                    commodity: newOffer.buy_commodity.name.toLowerCase(),
                    symbol: newOffer.currency.symbol,
                    price: formatNumber(newOffer.price),
                    grade: newOffer?.quality,
                  })}${
                    !demandLine && deliveryCostAmount(newOffer.delivery_cost) > 0
                      ? ` ${i18n.t("autoComment.proposalDeliveryCost", {
                          symbol: newOffer.currency.symbol,
                          amount: formatNumber(deliveryCostAmount(newOffer.delivery_cost)),
                        })}`
                      : ""
                  }`,
                }),
          uuid: uuidv4(),
          trader_market_offer_status:
            offer.status === OfferStatus.Negotiating
              ? offer.offer_type === OFFER_TYPES.MARKET_OFFER
                ? OfferStatus.Accepted
                : OfferStatus.OfferDone
              : offer.payment_time_option === OfferPaymentTimeOption.ReceiveUponExchange
                ? OfferStatus.ExchangeDone
                : OfferStatus.ExchangePaymentPending,
          send_mobile_notification: !(
            offer.status === OfferStatus.Negotiating &&
            offer.offer_type !== OFFER_TYPES.MARKET_OFFER
          ),
        })
      ).then(() =>
        dispatchAction(dispatch, listOfferComments({ trader_market_offer_id: offer.id }))
      );
    }

    if (
      offer.status !== OfferStatus.ExchangeDetailsUpdated &&
      offer.offer_type === OFFER_TYPES.DIRECT_OFFER
    ) {
      const currentDateTime = new Date();

      const response = await client.get(`${API_URL_DEPOSIT_DETAILS}${offer.deposit?.uuid}/`);
      const fullDepositData: Deposit = response.data;

      if (Number(negotiationProposal.minimum_quantity) < Number(fullDepositData?.quantity)) {
        const { remainder, used } = buildDepositSplit(
          fullDepositData,
          Number(negotiationProposal.minimum_quantity),
          currentDateTime
        );

        await dispatchAction(dispatch, updateDeposit(remainder.uuid, remainder));
        await dispatchAction(dispatch, updateDeposit(used.uuid, used));
      }

      const order = {
        uuid: uuidv4(),
        trader_market_offer: offer.id,
        deposit_uuid: fullDepositData?.uuid,
        aggregator_administrator: fullDepositData?.aggregator_administrator,
        picked_up_quantity: Number(negotiationProposal.minimum_quantity).toFixed(5),
        mobile_creation_time: currentDateTime.toISOString(),
        mobile_last_change_time: currentDateTime.toISOString(),
      };

      await dispatchAction(dispatch, createMarketOrder(order.uuid, order));
    }

    await dispatchAction(
      dispatch,
      updateMarketOffer(negotiationProposal.trader_market_offer, toOfferUpdatePayload(newOffer))
    );

    ownProps.setSeeReasonsOffer(newOffer);
    dispatchAction(dispatch, listRejections({ trader_market_offer_id: offer.id }));
    dispatchAction(dispatch, listRecalls({ trader_market_offer_id: offer.id }));
    dispatchAction(dispatch, listOfferNegotiationProposals({ trader_market_offer_id: offer.id }));
  },
  refreshContent: (offer: Offer) => {
    if (offer) {
      refreshOfferThread(dispatch, offer);
    }
  },
});

export const refreshOfferThread = (dispatch: AppDispatch, offer: Offer) => {
  dispatchAction(dispatch, listOfferCommentsReset());
  dispatchAction(dispatch, listOfferNegotiationProposalsReset());
  dispatchAction(dispatch, listRejections({ trader_market_offer_id: offer.id }));
  dispatchAction(dispatch, listRecalls({ trader_market_offer_id: offer.id }));
  dispatchAction(dispatch, listOfferComments({ trader_market_offer_id: offer.id }));
  dispatchAction(dispatch, listOfferNegotiationProposals({ trader_market_offer_id: offer.id }));
};

export const OfferCommentsModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(OfferCommentsModalView);
